import { FilteredCreditApplication, RequiredDocumentWithStatus, useGetContractIframeUrl } from '@applyonline/shared'
import { ContractIframeDto, IframeUrl } from '@applyonline/shared/types'
import { EApplicantType, EContractStatus, EFinancingProgram, EOriginSystemId } from '@if/core/types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  applicantFullName,
  hasApplicantIncomePendingReview,
  hasMissingDocuments,
} from '../../../../../data/credit-application-selectors'
import { contractIsReceived } from '../../hooks'
import CreditStep, { CreditStepStatus } from './CreditStep'

interface ContractCreditStepProps {
  applicantType: EApplicantType
  creditApp: FilteredCreditApplication
  skipBankAccountRequiredDocuments: RequiredDocumentWithStatus[]
  noPaymentPolicy: boolean
}
const ContractCreditStep = ({
  creditApp,
  skipBankAccountRequiredDocuments,
  applicantType,
  noPaymentPolicy,
}: ContractCreditStepProps) => {
  const { t } = useTranslation()

  const docStepDone = !hasMissingDocuments(creditApp)
  const applicant = applicantType === EApplicantType.Applicant ? creditApp.applicant : creditApp.coapplicant!

  const applicantIncomeStepDone =
    hasApplicantIncomePendingReview(creditApp, EApplicantType.Applicant, skipBankAccountRequiredDocuments) ||
    creditApp.applicant.areIncomesConfirmed

  const coapplicantIncomeStepDone =
    creditApp.coapplicant === null ||
    creditApp.coapplicant.areIncomesConfirmed ||
    hasApplicantIncomePendingReview(creditApp, EApplicantType.CoApplicant, skipBankAccountRequiredDocuments)

  const contractReceived = contractIsReceived(applicantType, creditApp)

  const isContractReady = creditApp.contract.status === EContractStatus.Ready

  const contractRefused = contractReceived && creditApp.contract.status === EContractStatus.Declined

  const isMerchantDashboardOrigin = creditApp.originSystemId === EOriginSystemId.MerchantDashboard

  const [shouldPoll, setShouldPoll] = useState(isContractReady)

  const [iframeDto, isLoadingLink, failureCount] = useGetContractIframeUrl<ContractIframeDto>(
    creditApp.id,
    EFinancingProgram.Personal,
    shouldPoll,
  )

  useEffect(() => {
    if (iframeDto || failureCount >= 4) setShouldPoll(false)
  }, [iframeDto, failureCount])

  const urlsDto = iframeDto?.urls?.find(
    (x: IframeUrl) => x.isForApplicant === (applicantType === EApplicantType.Applicant),
  )

  const hideLinkToDetail = contractReceived || !creditApp.cvtNumber || isLoadingLink || !urlsDto

  const hasCoApplicant = creditApp.coapplicant != null

  const applicantHasCompleted =
    iframeDto &&
    ((!urlsDto && iframeDto.completedSignatures?.length === 1) ||
      (!hasCoApplicant && iframeDto.completedSignatures?.length > 0) ||
      iframeDto.completedSignatures?.length > 1)

  const { status, content } = useMemo<{ status: CreditStepStatus; content?: string }>(() => {
    const defaultStatus = isContractReady ? CreditStepStatus.RequiresAttention : CreditStepStatus.NotReady
    const allStepsDone = docStepDone && applicantIncomeStepDone && coapplicantIncomeStepDone

    if (creditApp.contract.status === EContractStatus.NotReady && allStepsDone) {
      return {
        status: CreditStepStatus.Await,
        content: noPaymentPolicy
          ? t('overview.noPaymentPolicyContractWait')
          : isMerchantDashboardOrigin
            ? t('overview.providerContractWait')
            : t('overview.contractWait'),
      }
    }

    if (isContractReady || urlsDto) {
      return {
        status: applicantHasCompleted ? CreditStepStatus.Completed : defaultStatus,
        content: urlsDto ? t('overview.contractReady') : '',
      }
    }

    if (contractRefused) {
      return { status: CreditStepStatus.Await, content: t('contract.refused') }
    }

    if (contractReceived) {
      return { status: CreditStepStatus.Completed, content: `${t('contract.received')} ${t('contract.review')}` }
    }

    return { status: defaultStatus, content: '' }
  }, [
    creditApp,
    docStepDone,
    applicantIncomeStepDone,
    coapplicantIncomeStepDone,
    noPaymentPolicy,
    isMerchantDashboardOrigin,
    isContractReady,
    urlsDto,
    applicantHasCompleted,
    contractRefused,
    contractReceived,
    t,
  ])

  return (
    <CreditStep
      isPending={shouldPoll}
      status={status}
      linkToDetail={urlsDto?.url ?? ''}
      hideLinkToDetail={hideLinkToDetail}
      title={t('overview.contractStep', { name: applicantFullName(applicant) })}
      content={content ?? ''}
    />
  )
}

export default ContractCreditStep
